import { useState } from 'react'
import { router } from '@inertiajs/react'
import type { SalesminerAccount } from '@/types/serializers'
import type { SalesminerOpportunity } from '@/types/serializers'
import AccountLayout from '../_layout/AccountLayout.tsx'
import MetricsCards from '@/components/opportunities/MetricsCards'
import OpportunitiesGrid from '@/components/opportunities/OpportunitiesGrid'
import OpportunitiesSearch from '@/components/opportunities/OpportunitiesSearch'
import ResultsSummary from '@/components/opportunities/ResultsSummary'
import { salesminer_app_account_opportunity_path } from '@/routes'
import { Card } from "@/components/ui/card.tsx";

interface AccountOpportunitiesIndexProps {
    account: SalesminerAccount
    opportunities: SalesminerOpportunity[]
    search: string
    filters: {
        new: boolean
        starred: boolean
        relevant: boolean
        discarded: boolean
        exported: boolean
    }
    metrics: {
        total: number
        new: number
        starred: number
        relevant: number
        discarded: number
        exported: number
    }
}

export default function AccountOpportunitiesIndex({
                                                      account,
                                                      opportunities,
                                                      search,
                                                      filters,
                                                      metrics
                                                  }: AccountOpportunitiesIndexProps) {
    const [searchTerm, setSearchTerm] = useState(search)
    const [showFilters, setShowFilters] = useState(false)
    const [activeMetricFilter, setActiveMetricFilter] = useState<string | null>(null)

    // Extract organization slug from current URL
    const organizationSlug = window.location.pathname.split('/')[2]

    const handleSearch = (value: string) => {
        setSearchTerm(value)
        router.visit(window.location.pathname, {
            data: {search: value, filter: filters},
            only: ['opportunities', 'metrics'],
            preserveState: true
        })
    }

    const handleMetricCardClick = (filterKey: string) => {
        if (activeMetricFilter === filterKey) {
            setActiveMetricFilter(null)
            const newFilters = {new: false, starred: false, relevant: false, discarded: false, exported: false}
            router.visit(window.location.pathname, {
                data: {search: searchTerm, filter: newFilters},
                only: ['opportunities', 'metrics'],
                preserveState: true
            })
        } else {
            setActiveMetricFilter(filterKey)
            const newFilters = {
                new: filterKey === 'new',
                starred: filterKey === 'starred',
                relevant: filterKey === 'relevant',
                discarded: filterKey === 'discarded',
                exported: filterKey === 'exported'
            }
            router.visit(window.location.pathname, {
                data: {search: searchTerm, filter: newFilters},
                only: ['opportunities', 'metrics'],
                preserveState: true
            })
        }
    }

    return (
        <AccountLayout account={account} activeTab="opportunities">
            <Card className="pb-0 Create selector">
                <div
                    className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50 mb-2">
                        Account Opportunities
                    </h3>
                    <p className="text-slate-600 dark:text-neutral-300 mb-6">
                        Sales opportunities for {account.name}
                    </p>

                    {/* Key Metrics */}
                    <MetricsCards
                        metrics={metrics}
                        onMetricClick={handleMetricCardClick}
                        activeMetricFilter={activeMetricFilter}
                    />

                    {/* Search and Filter Bar */}
                    <OpportunitiesSearch
                        searchTerm={searchTerm}
                        onSearchChange={handleSearch}
                        showFilters={showFilters}
                        onToggleFilters={() => setShowFilters(!showFilters)}
                        placeholder="Search opportunities by name, pain point, or solution..."
                    />

                    {/* Filter Panel - Placeholder for now */}
                    {showFilters && (
                        <div
                            className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
                            <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-4">Advanced
                                Filters</h4>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Filter options will be
                                implemented
                                here...</p>
                        </div>
                    )}

                    {/* Results Summary */}
                    <ResultsSummary
                        count={opportunities.length}
                        searchTerm={searchTerm}
                        onClearSearch={() => handleSearch('')}
                    />

                    {/* Opportunities Grid */}
                    <OpportunitiesGrid
                        opportunities={opportunities}
                        onOpportunityClick={(id) => router.visit(salesminer_app_account_opportunity_path(organizationSlug, account.id, id))}
                        searchTerm={searchTerm}
                        emptyStateMessage={`No opportunities found for ${account.name}.`}
                    />
                </div>
            </Card>
        </AccountLayout>
    )
}